import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { DatePipe } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';

import { Router } from '@angular/router';
import { getTaskUrl } from '../../../../shared/task-navigation.util';
import { HttpClient } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { TabService } from '../../../../shared/tab.service';
import { PlanReviewerService } from '../../../../shared/plan-reviewer.service';
import { EnvironmentService } from '../../../../shared/environment.service';
import { TaskService } from '../../../../shared/task.service';
import { TaskCounterService } from '../../../../shared/task-counter.service';
import { FileMetadataService } from '../../../../shared/file-metadata.service';
import { ProjectCreationService } from '../../../../shared/project-creation.service';
import { ArchitecturalPlanPage } from '../../../../shared/models/architectural-plan-page.model';
import { marked } from 'marked';

/**
 * Details Tab Component - Displays AI-generated page explanation
 *
 * Shows the content from page-explanation.md with rich markdown formatting.
 * This provides comprehensive, educational explanations of architectural plan pages.
 *
 * Related: Issue #258 - AI-Powered Plan Page Explanation
 */
@Component({
  selector: 'app-details',
  imports: [MatCardModule, MatProgressSpinnerModule, MatIconModule, MatButtonModule, DatePipe],
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
})
export class DetailsComponent implements OnInit, OnDestroy {
  private tabService = inject(TabService);
  private planReviewerService = inject(PlanReviewerService);
  private taskService = inject(TaskService);
  private taskCounterService = inject(TaskCounterService);
  private fileMetadataService = inject(FileMetadataService);
  private projectCreationService = inject(ProjectCreationService);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);

  activePage: ArchitecturalPlanPage | null = null;
  modelStampDate: Date | null = null;
  explanationMarkdown: string | null = null;
  explanationHtml: string | null = null; // Rendered HTML
  loading = false;
  error: string | null = null;
  existingTaskId: string | null = null; // Track if there's an active ingestion task
  existingTaskStatus: string | null = null;

  private destroy$ = new Subject<void>();

  private http = inject(HttpClient);
  private environmentService = inject(EnvironmentService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    // Subscribe to active page changes
    this.tabService.activePlanPage$.pipe(takeUntil(this.destroy$)).subscribe(page => {
      this.activePage = page;
      this.modelStampDate = null;
      if (page) {
        const stamp = page.getModelStamp?.();
        const ts = stamp?.getCreatedTime?.();
        if (ts) {
          this.modelStampDate = new Date(ts.getSeconds() * 1000);
        }
        this.loadExplanation();
        this.checkForExistingTask();
      }
    });

    // Subscribe to active tasks to detect completion and refresh page data
    this.taskCounterService.activeTasks$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.activePage) {
        const previousTaskId = this.existingTaskId;
        this.checkForExistingTask();

        if (previousTaskId && !this.existingTaskId) {
          this.refreshPageAfterIngestion();
        }
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Checks if there's an active ingestion task for the current page.
   * Updates existingTaskId and existingTaskStatus for UI display.
   */
  private checkForExistingTask(): void {
    if (!this.activePage) {
      this.existingTaskId = null;
      this.existingTaskStatus = null;
      return;
    }

    const projectId = this.activePage.getArchitecturalPlanId();
    const pageNumber = this.activePage.getPageNumber();

    // Get all active tasks for this project
    const projectTasks = this.taskCounterService.getTasksForProject(projectId);

    // Find any active ingestion task for this specific page
    const pageTask = projectTasks.find(task => {
      // Check if task is for this page (metadata should have page_number)
      const taskPageNumber = task.metadata?.['page_number'] || task.metadata?.['pageNumber'];
      const isForThisPage = taskPageNumber === pageNumber;
      const isActive = task.status === 'pending' || task.status === 'processing';
      const isIngestionTask = task.taskType === 'plan_ingestion' || task.filename?.includes('Page');

      return isForThisPage && isActive && isIngestionTask;
    });

    if (pageTask) {
      this.existingTaskId = pageTask.taskId;
      this.existingTaskStatus = pageTask.status;
      ClientLoggingService.log(
        'INFO',
        `[Details] Found existing task for page ${pageNumber}:`,
        pageTask.taskId,
        pageTask.status
      );
    } else {
      this.existingTaskId = null;
      this.existingTaskStatus = null;
    }
  }

  private async loadExplanation() {
    if (!this.activePage) return;

    // Access explanationMarkdown from protobuf using getter method
    const explanationMarkdown = this.activePage.getExplanationMarkdown();

    ClientLoggingService.log('INFO', '[Details] activePage protobuf object:', this.activePage);
    ClientLoggingService.log(
      'INFO',
      '[Details] activePage.toObject():',
      (this.activePage as any).toObject ? (this.activePage as any).toObject() : 'NO toObject method'
    );
    ClientLoggingService.log('INFO', '[Details] getExplanationMarkdown():', explanationMarkdown);
    ClientLoggingService.log('INFO', '[Details] explanationMarkdown length:', explanationMarkdown.length);
    ClientLoggingService.log('INFO', '[Details] getPageMarkdown():', this.activePage.getPageMarkdown());
    ClientLoggingService.log('INFO', '[Details] Full page data:', {
      pageNumber: this.activePage.getPageNumber(),
      title: this.activePage.getTitle(),
      hasExplanation: !!explanationMarkdown,
      hasPageMarkdown: !!this.activePage.getPageMarkdown(),
    });

    if (explanationMarkdown) {
      // Explanation exists in proto, render it
      try {
        this.explanationMarkdown = explanationMarkdown;
        this.explanationHtml = await marked.parse(explanationMarkdown);
        this.loading = false;
        this.error = null; // Clear any previous errors

        ClientLoggingService.log('INFO', '[Details] After rendering:', {
          hasExplanationMarkdown: !!this.explanationMarkdown,
          hasExplanationHtml: !!this.explanationHtml,
          explanationHtmlLength: this.explanationHtml?.length,
          explanationHtmlPreview: this.explanationHtml?.substring(0, 100),
          loading: this.loading,
          error: this.error,
        });

        // Force change detection in case Angular didn't pick up the async change
        this.cdr.detectChanges();
      } catch (err) {
        ClientLoggingService.log('ERROR', '[Details] Error parsing markdown:', err);
        this.error = 'Error rendering explanation: ' + (err as Error).message;
        this.loading = false;
        this.explanationHtml = null;
      }
    } else {
      // No explanation yet - check if there's an active task or guide user to re-ingest
      if (this.existingTaskId) {
        // There's an active ingestion task for this page
        const statusText = this.existingTaskStatus === 'processing' ? 'in progress' : 'queued';
        this.error =
          `Page re-ingestion ${statusText}.\n\n` +
          `An ingestion task for this page is currently ${statusText}. ` +
          `The AI explanation will be generated as part of this task.\n\n` +
          `Click "View Task" below to monitor progress.`;
      } else {
        // No active task - guide user to re-ingest with explanation generation
        this.error =
          'AI explanation not yet generated for this page.\n\n' +
          'To generate an explanation, re-ingest this page with the "Generate AI Explanation" option enabled.\n\n' +
          'This will add 1-2 minutes to the ingestion process but creates comprehensive, ' +
          'educational content that explains architectural concepts, symbols, and code compliance.';
      }

      this.loading = false;
      this.explanationHtml = null; // Clear any previous HTML

      ClientLoggingService.log('INFO', '[Details] No explanation found:', {
        loading: this.loading,
        error: this.error,
        existingTaskId: this.existingTaskId,
        existingTaskStatus: this.existingTaskStatus,
      });
    }
  }

  /**
   * Re-fetches page data from the backend after ingestion completes,
   * then updates the shared TabService so all tabs see fresh data.
   */
  private async refreshPageAfterIngestion(): Promise<void> {
    if (!this.activePage) return;

    const planId = this.activePage.getArchitecturalPlanId();
    const pageNumber = this.activePage.getPageNumber();
    const pageFolderPath = this.activePage.getPageFolderPath();
    const fileIdMatch = pageFolderPath.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
    const fileId = fileIdMatch ? fileIdMatch[1] : undefined;

    ClientLoggingService.log(
      'INFO',
      `[Details] Ingestion task completed — refreshing page ${pageNumber} data from backend`
    );

    try {
      const freshPage = await this.planReviewerService.getPageWithFullContent(planId, pageNumber, fileId);
      this.activePage = freshPage;
      this.tabService.setActivePlanPage(freshPage);
      await this.loadExplanation();
    } catch (error) {
      ClientLoggingService.log('ERROR', '[Details] Failed to refresh page after ingestion:', error);
    }
  }

  private getExplanationPath(): string {
    if (!this.activePage) return '';

    // Note: fileId is not in the proto, it's derived from pageFolderPath
    const pageFolderPath = this.activePage.getPageFolderPath();
    const pageNumber = this.activePage.getPageNumber();
    const planId = this.activePage.getArchitecturalPlanId();

    // Extract fileId from pageFolderPath if present
    const fileIdMatch = pageFolderPath.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
    if (fileIdMatch) {
      const fileId = fileIdMatch[1];
      return `projects/${planId}/files/${fileId}/pages/${String(pageNumber).padStart(3, '0')}/page-explanation.md`;
    } else {
      // Legacy structure
      return `projects/${planId}/pages/${String(pageNumber).padStart(3, '0')}/page-explanation.md`;
    }
  }

  /**
   * Navigates to the Tasks page to view the active ingestion task.
   */
  viewTask(): void {
    if (!this.activePage) return;

    const projectId = this.activePage.getArchitecturalPlanId();
    this.router.navigate(['/projects', projectId, 'tasks']);
  }

  /**
   * Triggers page re-ingestion with explanation generation enabled.
   * Reuses the same infrastructure as drawer checkbox re-ingestion.
   */
  async generateExplanation() {
    if (!this.activePage) return;

    const projectId = this.activePage.getArchitecturalPlanId();
    const pageNumber = this.activePage.getPageNumber();

    // Extract file ID from page folder path (mirrors drawer.component.ts logic).
    // Legacy single-file projects don't have /files/{id}/ in path; default to "1".
    const pageFolderPath = this.activePage.getPageFolderPath();
    const fileIdMatch = pageFolderPath.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
    const fileId = fileIdMatch ? fileIdMatch[1] : '1';
    const isLegacy = !fileIdMatch;

    this.loading = true;

    try {
      const filename = await this.getFilenameForFileId(fileId, projectId, isLegacy);

      if (!filename) {
        this.snackBar.open('Cannot determine source filename. Re-ingestion not available.', 'Close', {
          duration: 5000,
        });
        this.loading = false;
        return;
      }

      ClientLoggingService.log(
        'INFO',
        `[Details] Starting re-ingestion for page ${pageNumber} with explanation generation`
      );

      // Start async ingestion with explanation generation enabled
      const response = await this.taskService
        .startAsyncIngestFile(
          projectId,
          filename,
          pageNumber,
          true, // forceReprocess
          true, // enableOrientationDetection
          true // generateExplanation ← KEY: Generate explanation!
        )
        .toPromise();

      if (response) {
        const taskId = response.getTaskId();

        // Add task to counter for top app bar tracking
        this.taskCounterService.addTask(taskId, projectId, `${filename} (Page ${pageNumber}) with AI Explanation`);

        ClientLoggingService.log('INFO', `[Details] Task started: ${taskId}`);

        // Show snackbar with "View Task" action to open the started task
        const snackBarRef = this.snackBar.open(
          'Page re-ingestion started with AI explanation generation. This will take 2-4 minutes.',
          'View Task',
          { duration: 8000 }
        );

        snackBarRef.onAction().subscribe(() => {
          this.router.navigate(getTaskUrl(projectId, taskId));
        });

        // Clear error state
        this.error = null;
        this.loading = false;
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[Details] Error starting re-ingestion:', error);
      this.snackBar.open('Error starting page re-ingestion', 'Close', { duration: 5000 });
      this.loading = false;
    }
  }

  /**
   * Gets the source filename for a file ID using FileMetadataService.
   * For legacy projects (isLegacy=true), falls back to listFileMetadata and
   * then direct GCS listing when getFileMetadata cannot resolve fileId "1".
   * For modern multi-file projects the fallbacks are skipped to avoid
   * accidentally re-ingesting against the wrong source file.
   */
  private async getFilenameForFileId(fileId: string, projectId: string, isLegacy: boolean): Promise<string | null> {
    try {
      const response = await this.fileMetadataService.getFileMetadata(projectId, fileId, false).toPromise();

      if (response && response.getMetadata()) {
        const metadata = response.getMetadata();
        const fileName = metadata?.getFileName() || null;
        if (fileName) return fileName;
      }
    } catch (error) {
      ClientLoggingService.log('INFO', '[Details] getFileMetadata failed:', error);
    }

    if (!isLegacy) {
      return null;
    }

    // Legacy fallback 1: list metadata and use the first file
    try {
      const listResponse = await this.fileMetadataService.listFileMetadata(projectId).toPromise();
      const files = listResponse?.getFilesList() || [];
      if (files.length > 0) {
        const fileName = files[0].getFileName() || null;
        if (fileName) return fileName;
      }
    } catch (error) {
      ClientLoggingService.log('INFO', '[Details] listFileMetadata failed, trying GCS listing:', error);
    }

    // Legacy fallback 2: list input files directly from GCS (works for projects
    // that predate the InputFileMetadata system)
    try {
      const result = await this.projectCreationService.getUploadedFiles(projectId);
      if (result.success && result.files && result.files.length > 0) {
        return result.files[0].name;
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', '[Details] Error listing project input files:', error);
    }

    return null;
  }
}
